import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { SimulationResponse, SimulationResults, SimulationSearchFilters } from '../../core/models/simulation.model';
import { FilterOptionsPopupComponent } from '../../common/popups/filter-options-popup/filter-options-popup.component';
import { INITIAL_SIMULATION_FILTER_STATE } from '../../core/consts/initial-simulation-filters.const';
import { RemoveExtensionPipe } from '../../common/pipes/remove-extension.pipe';
import { sanitizeSearchString } from '../../utils/sanitize.util';

interface UpscalingFluidFlowModelRow {
  simulationId: string;
  name: string;
  directionX: string;
  directionY: string;
  directionZ: string;
  rockFile: string;
  upscalingRockModel: string;
  user: string;
  createdAt: Date;
  completedAt: Date;
}

@Component({
  selector: 'app-upscaling-fluid-flow-models',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './upscaling-fluid-flow-models.component.html',
  styleUrl: './upscaling-fluid-flow-models.component.scss',
})
export class UpscalingFluidFlowModelsComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = [
    'name',
    'directionX',
    'directionY',
    'directionZ',
    'view',
    'rockFile',
    'upscalingRockModel',
    'user',
    'createdAt',
    'completedAt',
  ];

  dataSource: UpscalingFluidFlowModelRow[] = [
  ];

  searchBy: string = 'name';
  searchString = '';

  isLoading = false;
  count = 0;
  pageSize = 10;
  pageIndex = 0;

  filterData: SimulationSearchFilters = JSON.parse(JSON.stringify(INITIAL_SIMULATION_FILTER_STATE));

  private readonly runStatuses: string[] = [
    'Success',
    'Failure',
    'Waiting',
    'Initializing',
    'Processing',
    'Canceled',
  ];

  selectedRow: UpscalingFluidFlowModelRow | null = null;

  constructor(
    private readonly simulationsService: SimulationsService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
  ) {
    // Restrict types list to UpscalingKabs only
    this.filterData.types = (this.filterData.types as any[]).filter(
      (t: any) => t.name === 'UpscalingKabs',
    );
    // Start with both selected; user can then select either, both, or none in the popup
    (this.filterData.types as any[]).forEach((t: any) => {
      t.checked = true;
    });
    this.filterData.filterType = 'Name';
  }

  ngOnInit(): void {
    this.fetchSimulations();
  }

  onFilterClick(): void {
    const dialogRef = this.dialog.open(FilterOptionsPopupComponent, {
      disableClose: true,
      data: {
        ...this.filterData,
        statuses: this.runStatuses,
        hideTypes: true,
      },
    });

    dialogRef.afterClosed().subscribe((result: SimulationSearchFilters | null) => {
      if (!result) return;
      const typesArray = (result.types as any[]) || [];
      // Ensure at least one type (UpscalingKabs) stays selected
      if (typesArray.length && !typesArray.some((t: any) => t.checked)) {
        typesArray.forEach((t: any) => {
          t.checked = true;
        });
        result.types = typesArray;
      }

      this.filterData = {
        ...this.filterData,
        ...result,
      };
      this.pageIndex = 0;
      this.fetchSimulations();
    });
  }

  onSearchClick(): void {
    this.pageIndex = 0;
    this.fetchSimulations();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchSimulations();
  }

  onViewResults(row: UpscalingFluidFlowModelRow): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/simulation-results/kabs', row.simulationId]),
    );
    window.open(url, '_blank');
  }

  onViewRockFile(row: UpscalingFluidFlowModelRow): void {
    // Navigation to rock visualization will be wired later
  }

  onViewRockModel(row: UpscalingFluidFlowModelRow): void {
    // Navigation to rock model manager will be wired later
  }

  onRowClick(row: UpscalingFluidFlowModelRow): void {
    this.selectedRow = row;
  }

  goToFluidFlowModelsManager(): void {
    if (!this.selectedRow) return;
    this.router.navigate(['/upscaling/fluid-flow-manager'], {
      queryParams: {
        simulationId: this.selectedRow.simulationId,
        simulationName: this.selectedRow.name,
        rockFile: this.selectedRow.rockFile,
        rockModel: this.selectedRow.upscalingRockModel,
      },
    });
  }

  private fetchSimulations(): void {
    this.isLoading = true;
    const filterType =
      this.searchBy === 'user'
        ? 'User'
        : this.searchBy === 'rockFile'
        ? 'File'
        : this.searchBy === 'rockModel'
        ? 'Name'
        : 'Name';

    const selectedTypes =
      (this.filterData.types as any[])
        ?.filter((t: any) => t.checked)
        .map((t: any) => t.name) || [];

    const selectedStatuses =
      (this.filterData.runStatus as any[])
        ?.filter((s: any) => s.checked)
        .map((s: any) => s.name) || [];

    const filterData: SimulationSearchFilters = {
      filterType,
      searchString: sanitizeSearchString(this.searchString),
      types: selectedTypes,
      runStatus: selectedStatuses,
      directions: {
        ...this.filterData.directions,
      },
      startDate: this.formatDate(this.filterData.startDate as any),
      endDate: this.formatDate(this.filterData.endDate as any),
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
    };

    this.simulationsService
      .getFilteredSimulations(filterData)
      .subscribe({
        next: (result: { count: number; data: SimulationResponse<SimulationResults>[] }) => {
          this.count = result.count;
          this.dataSource = result.data.map((sim) => this.mapSimulationToRow(sim));
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private mapSimulationToRow(sim: SimulationResponse<SimulationResults>): UpscalingFluidFlowModelRow {
    const getDirectionStatus = (direction: 'x' | 'y' | 'z'): string =>
      sim.results?.find((r) => r.direction.toLowerCase() === direction)?.status || '—';

    const rockFileModelName = (sim as any).rockFileModelName as string | undefined;

    return {
      simulationId: sim.id,
      name: sim.name,
      directionX: getDirectionStatus('x'),
      directionY: getDirectionStatus('y'),
      directionZ: getDirectionStatus('z'),
      rockFile: sim.fileName,
      upscalingRockModel: rockFileModelName || sim.parameters?.name || '—',
      user: sim.userEmail,
      createdAt: new Date(sim.createdAt),
      completedAt: new Date((sim as any).completedAt ?? sim.createdAt),
    };
  }

  private formatDate(dateValue: any): string | null {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }
}

